/* SOUHLAS S POŘADÍM CÍLŮ — „Schod po schodu“ (Č12, schváleno 4. 10. 2026).
 * ============================================================================
 *
 * Klient na konci přehrání odpoví na „Souhlasíte s tímhle pořadím?“ —
 * „Ano“, nebo pořadí upraví. Zapíše se do `d.souhlas.poradi` i se SNÍMKEM
 * toho, co viděl (audit ZPKT/IDD: nestačí „klient řekl ano“, musí být
 * dohledatelné, NA CO): pořadí, „všechno stejně důležité“, částka odkládání
 * a měna, pravidlo výpočtu, výsledek každého cíle, renta i pesimisticky,
 * razítko metodiky plánu a verze výpočtu.
 *
 * U KAŽDÉHO VÝSLEDKU I NÁZEV A ČÁSTKA (dočištění A3, 4. 10. 2026): samotné id
 * nestačí — když poradce cíl později přejmenuje nebo smaže, už by se
 * nedohledalo, k čemu klient řekl Ano. Proto `popis` (jak se cíl jmenoval),
 * `cilova` (kolik měl stát), `chybi` (kolik k termínu chybělo) a u renty
 * `rentaMes` (měsíční renta, ze které se počítalo). Neznámé = null, ne 0.
 *
 * ⚠️ ČÍSLA PLÁNU SE TÍM NEMĚNÍ. Pořadí od klienta je jen zápis. Solver sice
 * umí číst `cil.priorita`, ale v aplikaci ho nikde nikdo nenastavuje — a kdyby
 * ho začala nastavovat analýza, pohnula by čísly uložených plánů (pravidlo
 * majitele 3. 10. 2026: staré plány se nepřepočítávají). Jestli má pořadí
 * klienta měnit výpočet Návrhu, rozhodne majitel.
 *
 * Tenhle soubor schválně NEIMPORTUJE nic těžkého (plán, solver) — volá ho
 * `normalizujSouhlasy` v lib/analyzaV2/model.js při každém načtení analýzy.
 */

/** Odpovědi: „ano“ = souhlas s doporučeným pořadím, „upraveno“ = klient pořadí změnil. */
export const ODPOVEDI_PORADI = Object.freeze(["ano", "upraveno"]);
/** Jak se plnění počítá — „k termínu“ = jako Návrh řešení (cíl dostává peníze jen do svého termínu). */
export const PRAVIDLO_K_TERMINU = "kTerminu";
/** Odkud byla částka odkládání: dnešní pravidelné vklady, nebo přebytek z plánu. */
export const VOLBY_ODKLADANI = Object.freeze(["dnes", "plan"]);

/* Strop velikosti snímku — je to zápis jedné odpovědi, ne kopie plánu.
 * Rozbitá data z API (pole s tisíci položek) se tím nenafouknou. */
const MAX_CILU = 40;
const MAX_TEXT = 80;

const text = (v, max = MAX_TEXT) => {
  if (v === null || v === undefined) return null;
  if (typeof v !== "string" && typeof v !== "number") return null;
  const s = String(v).trim().slice(0, max);
  return s === "" ? null : s;
};
const cislo = (v) => (v !== null && v !== undefined && v !== "" && typeof v !== "boolean" && Number.isFinite(+v) ? +v : null);
const procento = (v) => {
  const n = cislo(v);
  return n === null ? null : Math.min(999, Math.max(0, Math.round(n)));
};
const rok = (v) => {
  const n = cislo(v);
  return n === null || n < 1900 || n > 2200 ? null : Math.round(n);
};
const datum = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

/** Částka do snímku: nezáporná, celé koruny; neznámá → null (ne 0). */
const castkaSn = (v) => {
  const n = cislo(v);
  return n === null ? null : Math.max(0, Math.round(n));
};

function vysledek(v) {
  if (!v || typeof v !== "object") return null;
  const id = text(v.id);
  if (!id) return null;
  const nezadano = v.nezadano === "castka" || v.nezadano === "termin" ? v.nezadano : null;
  const rentaMes = castkaSn(v.rentaMes);
  return {
    id,
    popis: text(v.popis),
    cilova: nezadano === "castka" ? null : castkaSn(v.cilova),
    chybi: nezadano ? null : castkaSn(v.chybi),
    pct: nezadano ? null : procento(v.pct),
    splneno: v.splneno === true,
    rok: rok(v.rok),
    ...(rentaMes !== null ? { rentaMes } : {}),
    ...(nezadano ? { nezadano } : {}),
  };
}

/**
 * Uložená hodnota → platný snímek, nebo `null` (nezadáno). Nikdy nespadne:
 * cokoli rozbitého se zahodí, neplatné číslo je `null`, ne 0.
 * @param {*} x `d.souhlas.poradi`
 */
export function normalizujSouhlasPoradi(x) {
  if (!x || typeof x !== "object" || Array.isArray(x)) return null;
  if (!ODPOVEDI_PORADI.includes(x.odpoved)) return null;
  const poradi = Array.isArray(x.poradi)
    ? x.poradi.map((id) => text(id)).filter(Boolean).slice(0, MAX_CILU)
    : [];
  const o = x.odkladani && typeof x.odkladani === "object" ? x.odkladani : {};
  const castka = cislo(o.castkaMes);
  return {
    odpoved: x.odpoved,
    kdy: datum(x.kdy),
    poradi,
    rovne: x.rovne === true,
    odkladani: {
      volba: VOLBY_ODKLADANI.includes(o.volba) ? o.volba : null,
      castkaMes: castka === null ? null : Math.max(0, Math.round(castka)),
    },
    mena: text(x.mena, 3),
    pravidlo: x.pravidlo === PRAVIDLO_K_TERMINU ? PRAVIDLO_K_TERMINU : null,
    metodika: text(x.metodika, 20),
    vysledky: Array.isArray(x.vysledky) ? x.vysledky.map(vysledek).filter(Boolean).slice(0, MAX_CILU) : [],
    rentaPct: procento(x.rentaPct),
    rentaPctPesimisticky: procento(x.rentaPctPesimisticky),
    verzeVypoctu: text(x.verzeVypoctu, 40),
  };
}
